import { useMemo } from 'react';
import { useAllReminders } from './useAllReminders';
import { useAllPayments, type Payment } from './useAllPayments';
import { type Reminder } from '../types/reminders';

export type UnifiedItem =
  | (Reminder & { type: 'reminder' })
  | (Payment & { type: 'payment' });

export type UnifiedItems = {
  today: UnifiedItem[];
  upcoming: UnifiedItem[];
  overdue: UnifiedItem[];
};

export function useUnifiedRemindersPayments() {
  const { reminders, isLoading: remindersLoading } = useAllReminders();
  const { payments, isLoading: paymentsLoading } = useAllPayments();

  const unified = useMemo((): UnifiedItems => {
    // Add type discriminator to reminders
    const remindersWithType: UnifiedItem[] = [];

    // Process reminders from all categories (excluding paid payments)
    ['today', 'upcoming', 'overdue'].forEach((category) => {
      const categoryKey = category as 'today' | 'upcoming' | 'overdue';
      reminders[categoryKey].forEach((reminder) => {
        // Use the original dueAt ISO timestamp
        remindersWithType.push({
          ...reminder,
          type: 'reminder',
        });
      });
    });

    // Process payments from all categories except 'paid'
    const paymentsWithType: UnifiedItem[] = [];
    ['today', 'upcoming', 'overdue'].forEach((category) => {
      const categoryKey = category as 'today' | 'upcoming' | 'overdue';
      payments[categoryKey].forEach((payment) => {
        paymentsWithType.push({
          ...payment,
          type: 'payment',
        });
      });
    });

    // Combine all items
    const allItems = [...remindersWithType, ...paymentsWithType];

    // Re-categorize based on dueAt to ensure consistency
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const categorized: UnifiedItems = {
      today: [],
      upcoming: [],
      overdue: [],
    };

    allItems.forEach((item) => {
      const dueDate = new Date(item.dueAt);

      if (dueDate < today) {
        categorized.overdue.push(item);
      } else if (dueDate >= today && dueDate < tomorrow) {
        categorized.today.push(item);
      } else {
        categorized.upcoming.push(item);
      }
    });

    // Sort each category by dueAt (ascending)
    categorized.today.sort(
      (a, b) => new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime()
    );
    categorized.upcoming.sort(
      (a, b) => new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime()
    );
    categorized.overdue.sort(
      (a, b) => new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime()
    );

    return categorized;
  }, [reminders, payments]);

  return {
    unified,
    isLoading: remindersLoading || paymentsLoading,
  };
}
